import * as React from 'react';
import { User } from 'lucide-react';

export type AvatarSize = 'small' | 'medium' | 'large' | 'extra-large';

export interface AvatarProps {
  /**
   * Size of the avatar
   */
  size?: AvatarSize;
  /**
   * Image source URL
   */
  src?: string;
  /**
   * Initials to display (2 characters) when no image is provided
   */
  initials?: string;
  /**
   * Alt text for the image
   */
  alt?: string;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

const sizeConfig = {
  small: {
    size: 20,
    fontSize: 0, // Too small for initials
    fontWeight: 400,
    iconSize: 12,
    borderRadius: '99px',
  },
  medium: {
    size: 32,
    fontSize: 13,
    fontWeight: 400,
    iconSize: 16,
    borderRadius: '99px',
  },
  large: {
    size: 48,
    fontSize: 13,
    fontWeight: 400,
    iconSize: 24,
    borderRadius: '4px',
  },
  'extra-large': {
    size: 96,
    fontSize: 27,
    fontWeight: 600,
    iconSize: 48,
    borderRadius: '8px',
  },
};

/**
 * Avatar component - Arbor Design System
 *
 * Displays a user avatar with support for images, initials, or a placeholder icon.
 * Features hover state with inner shadow effect.
 */
export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      size = 'medium',
      src,
      initials,
      alt = 'Avatar',
      className,
      style,
      'data-testid': dataTestId,
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const [imageError, setImageError] = React.useState(false);
    const config = sizeConfig[size];

    // Only apply hover state for small size
    const hasHoverState = size === 'small';

    const containerStyles: React.CSSProperties = {
      width: `${config.size}px`,
      height: `${config.size}px`,
      borderRadius: config.borderRadius,
      border: '1px solid #efefef',
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: '#f8f8f8',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      cursor: 'default',
      boxSizing: 'border-box',
      ...style,
    };

    const hoverOverlayStyles: React.CSSProperties = {
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      boxShadow: isHovered && hasHoverState ? 'inset 0px 4px 100px 0px rgba(32, 32, 32, 0.3)' : 'none',
      borderRadius: config.borderRadius,
      transition: 'box-shadow 0.2s ease-in-out',
    };

    const imageStyles: React.CSSProperties = {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    };

    const initialsStyles: React.CSSProperties = {
      fontSize: `${config.fontSize}px`,
      fontWeight: config.fontWeight,
      color: '#2f2f2f',
      lineHeight: 1.5,
      textAlign: 'center',
      userSelect: 'none',
    };

    // Determine what to display
    const showImage = src && !imageError;
    const showInitials = !showImage && initials && size !== 'small';
    const showPlaceholder = !showImage && !showInitials;

    return (
      <div
        ref={ref}
        className={className}
        style={containerStyles}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid={dataTestId}
      >
        {showImage && (
          <img
            src={src}
            alt={alt}
            style={imageStyles}
            onError={() => setImageError(true)}
          />
        )}

        {showInitials && (
          <span style={initialsStyles}>
            {initials.slice(0, 2).toUpperCase()}
          </span>
        )}

        {showPlaceholder && (
          <User size={config.iconSize} color="#d1d1d1" strokeWidth={2} />
        )}

        <div style={hoverOverlayStyles} />
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';
